import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/ui/PageHeader';
import { unknownWordsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import SpeakButton from '../components/ui/SpeakButton';
import {
  BookmarkIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ArrowPathIcon,
  ListBulletIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

const STATUS_FILTERS = [
  { id: 'all', label: 'Të gjitha' },
  { id: 'new', label: 'E re' },
  { id: 'learning', label: 'Duke mësuar' },
  { id: 'known', label: 'E mësuar' },
];

const STATUS_LABELS = {
  new: 'E re',
  learning: 'Duke mësuar',
  known: 'E mësuar',
};

const STATUS_BADGE = {
  new: 'default',
  learning: 'secondary',
  known: 'success',
};

const Flashcard = ({ item, updateStatus, remove, busyId }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <div 
      onClick={() => setFlipped(!flipped)}
      className="h-48 w-full cursor-pointer select-none"
      style={{ perspective: '1000px' }}
    >
      <div 
        className="relative w-full h-full duration-500 rounded-xl shadow-sm border border-zinc-200 bg-white transition-all transform-gpu"
        style={{ 
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front Side */}
        <div 
          className="absolute inset-0 w-full h-full p-5 flex flex-col justify-between rounded-xl bg-gradient-to-br from-white to-zinc-50/50"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Gjermanisht</span>
            {item.level && (
              <Badge className="bg-zinc-950 text-white font-bold">{item.level.toUpperCase()}</Badge>
            )}
          </div>
          <div className="my-auto flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="text-xl font-extrabold text-zinc-900 text-center select-all">{item.word}</span>
            <SpeakButton text={item.word} lang="de-DE" />
          </div>
          <div className="text-[10px] text-zinc-400 text-center">Kliko për ta rrotulluar</div>
        </div>

        {/* Back Side */}
        <div 
          className="absolute inset-0 w-full h-full p-5 flex flex-col justify-between rounded-xl bg-zinc-900 text-white"
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Përkthimi & Konteksti</span>
            <span className="text-[10px] text-zinc-400">Kliko për të parë fjalën</span>
          </div>
          <div className="my-auto text-center space-y-2">
            <p className="text-base font-bold text-emerald-400">{item.context || '—'}</p>
            {item.notes && <p className="text-xs text-zinc-300 italic">Shënim: {item.notes}</p>}
          </div>
          <div className="flex justify-between items-center mt-2" onClick={(e) => e.stopPropagation()}>
            <select
              value={item.status}
              disabled={busyId === item.id}
              onChange={(e) => updateStatus(item.id, e.target.value)}
              className="text-xs border border-zinc-700 bg-zinc-800 text-white rounded px-2 py-1 focus:outline-none"
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value} className="bg-zinc-950 text-white">
                  {label}
                </option>
              ))}
            </select>
            <button
              disabled={busyId === item.id}
              onClick={() => remove(item.id)}
              className="p-1.5 text-zinc-400 hover:text-red-400 rounded-full hover:bg-zinc-800 transition-colors"
              title="Fshi"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UnknownWordsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'list' or 'cards'

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await unknownWordsAPI.list();
      setItems(res.data?.items || []);
    } catch (e) {
      setError('S’u ngarkuan fjalët.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, learning: 0, known: 0 };
    items.forEach((it) => {
      if (c[it.status] !== undefined) c[it.status] += 1;
    });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter !== 'all' && it.status !== statusFilter) return false;
      if (!q) return true;
      return (
        it.word.toLowerCase().includes(q)
        || (it.context || '').toLowerCase().includes(q)
        || (it.notes || '').toLowerCase().includes(q)
      );
    });
  }, [items, statusFilter, search]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      const res = await unknownWordsAPI.update(id, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? res.data : it)));
    } catch {
      setError('Nuk u përditësua statusi.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    setBusyId(id);
    try {
      await unknownWordsAPI.remove(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch {
      setError('Nuk u fshi fjala.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="page-shell-wide space-y-6">
        <PageHeader
          icon={BookmarkIcon}
          badge="Përsëritje personale"
          title="Fjalët e mia për t’i mësuar"
          description="Këtu i gjen fjalët që ke ruajtur gjatë mësimit. Ktheji në karta, dëgjo shqiptimin dhe shëno përparimin tënd."
        >
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge>{counts.all} gjithsej</Badge>
            <Badge variant="success">{counts.known} të mësuara</Badge>
            <Badge>{counts.learning} në përsëritje</Badge>
            <Badge>{counts.new} të reja</Badge>
          </div>
        </PageHeader>

        <Card>
          <CardHeader className="pb-4 border-b border-zinc-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle>Rishiko fjalët</CardTitle>
                <CardDescription>Zgjidh një fjalë, provo ta kujtosh dhe pastaj kontrollo përkthimin.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex bg-zinc-100 p-0.5 rounded-lg border border-zinc-200">
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      viewMode === 'list'
                        ? 'bg-white text-zinc-900 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    <ListBulletIcon className="w-3.5 h-3.5" />
                    Listë
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('cards')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      viewMode === 'cards'
                        ? 'bg-white text-zinc-900 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    <BookOpenIcon className="w-3.5 h-3.5" />
                    Karta
                  </button>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                  <ArrowPathIcon className="w-4 h-4" />
                  Rifresko
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setStatusFilter(f.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      statusFilter === f.id
                        ? 'bg-zinc-900 text-white border-zinc-400 shadow-sm'
                        : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300'
                    }`}
                  >
                    {f.label} ({counts[f.id] ?? 0})
                  </button>
                ))}
              </div>

              <div className="relative w-full md:max-w-xs">
                <MagnifyingGlassIcon className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Kërko fjalë ose përkthim..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                {error}
              </p>
            )}

            {loading ? (
              <div className="text-center py-16">
                <ArrowPathIcon className="w-8 h-8 text-zinc-300 mx-auto mb-2 animate-spin" />
                <p className="text-sm text-zinc-500">Duke u ngarkuar...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <BookmarkIcon className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-sm font-semibold text-zinc-700 mb-1">
                  {items.length === 0 ? 'Nuk ke fjalë të ruajtura ende' : 'Asnjë rezultat për këtë filtër'}
                </p>
                <p className="text-xs text-zinc-500 mb-6">
                  Shko te fjalori dhe kliko ikonën &quot;Ruaj&quot; pranë fjalëve që nuk i di.
                </p>
                <Button onClick={() => navigate('/vocabulary')}>Shko te Fjalori</Button>
              </div>
            ) : viewMode === 'cards' ? (
              /* Grid of Flippable Flashcards */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((it) => (
                  <Flashcard
                    key={it.id}
                    item={it}
                    updateStatus={updateStatus}
                    remove={remove}
                    busyId={busyId}
                  />
                ))}
              </div>
            ) : (
              /* Modern List View with active recall blur */
              <div className="space-y-4">
                <div className="text-xs text-zinc-400 italic mb-2">
                  * Këshillë: Kaloni mausin ose klikoni mbi pjesën e mjegulluar te përkthimi për ta zbuluar fjalën shqip.
                </div>
                <div className="overflow-x-auto rounded-lg border border-zinc-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50/70 hover:bg-zinc-50/70">
                        <TableHead className="w-[30%]">Fjala (Gjermanisht)</TableHead>
                        <TableHead className="w-[35%]">Përkthimi (Kliko për ta zbuluar)</TableHead>
                        <TableHead className="w-[15%]">Statusi</TableHead>
                        <TableHead className="w-[10%] text-right">Veprim</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((it) => (
                        <TableRow key={it.id} className="hover:bg-zinc-50/50">
                          <TableCell className="font-bold text-zinc-900">
                            <div className="flex items-center gap-2">
                              <span>{it.word}</span>
                              <SpeakButton text={it.word} lang="de-DE" />
                            </div>
                          </TableCell>
                          <TableCell>
                            {/* Blur recall translation */}
                            <span 
                              className="blur-[4px] hover:blur-none focus:blur-none active:blur-none transition-all duration-300 bg-zinc-100/80 px-2.5 py-1.5 rounded text-sm text-zinc-800 cursor-pointer select-none hover:select-text"
                              title="Zzbulo përkthimin"
                            >
                              {it.context || '—'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <select
                                value={it.status}
                                disabled={busyId === it.id}
                                onChange={(e) => updateStatus(it.id, e.target.value)}
                                className="text-xs border border-zinc-200 rounded-md px-2 py-1.5 bg-white cursor-pointer hover:border-zinc-300 focus:outline-none"
                              >
                                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                              <Badge variant={STATUS_BADGE[it.status] || 'default'} className="hidden lg:inline-flex text-[10px] font-bold">
                                {STATUS_LABELS[it.status] || it.status}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyId === it.id}
                              onClick={() => remove(it.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-zinc-200"
                            >
                              <TrashIcon className="w-4 h-4" />
                              Fshi
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default UnknownWordsPage;
